import { apiClient } from '../api/apiClient';
import { ContactFormData, ContactFormResponse, SpamProtectionData } from './contact.types';

/**
 * Contact Form Service
 * Handles contact form submissions with spam protection
 */
class ContactFormService {
  private cachedToken: SpamProtectionData | null = null;

  /**
   * Get form token for spam protection
   */
  async getFormToken(): Promise<SpamProtectionData | null> {
    try {
      // Return cached token if still valid (with 1 minute buffer)
      if (this.cachedToken && this.cachedToken.expiresAt > Date.now() + 60000) {
        return this.cachedToken;
      }

      const response = await apiClient.get<{
        success: boolean;
        token: string;
        challenge: string;
        expiresAt: number;
      }>('/contact/token');

      if (response.data.success) {
        // Parse challenge and calculate answer
        const parts = response.data.challenge.split('+').map(Number);
        const num1 = parts[0] ?? 0;
        const num2 = parts[1] ?? 0;
        const challengeAnswer = String(num1 + num2);

        this.cachedToken = {
          formToken: response.data.token,
          challenge: response.data.challenge,
          challengeAnswer,
          expiresAt: response.data.expiresAt,
        };

        return this.cachedToken;
      }

      return null;
    } catch (error) {
      console.error('Failed to get form token:', error);
      return null;
    }
  }

  /**
   * Submit contact form with spam protection data
   */
  async submitContactForm(
    data: ContactFormData,
    spamProtection: {
      formToken?: string;
      challengeAnswer?: string;
      mouseMovements: number;
      keystrokes: number;
      formLoadTime: number;
    }
  ): Promise<ContactFormResponse> {
    try {
      const response = await apiClient.post<ContactFormResponse>('/contact', {
        ...data,
        _formToken: spamProtection.formToken,
        _challenge: spamProtection.challengeAnswer,
        _mouseMovements: spamProtection.mouseMovements,
        _keystrokes: spamProtection.keystrokes,
        _formLoadTime: spamProtection.formLoadTime,
      });

      // Clear cached token after submission
      this.cachedToken = null;

      return response.data;
    } catch (error: any) {
      console.error('Contact form submission error:', error);

      // Handle rate limit errors
      if (error.response?.status === 429) {
        return {
          success: false,
          error: error.response.data?.error || 'Zu viele Anfragen. Bitte versuchen Sie es später erneut.',
        };
      }

      // Handle validation errors
      if (error.response?.status === 400) {
        return {
          success: false,
          error: error.response.data?.error || 'Ungültige Eingabe. Bitte überprüfen Sie Ihre Daten.',
        };
      }

      // Handle server errors
      return {
        success: false,
        error: 'Fehler beim Senden der Nachricht. Bitte versuchen Sie es später erneut.',
      };
    }
  }
}

export const contactFormService = new ContactFormService();
